import React, { Suspense, lazy, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowUpRight,
    ArrowRight,
    Landmark,
    HeartPulse,
    Factory,
    Zap,
    ShoppingBag,
    Plane,
    GraduationCap,
    Building2,
    Radio,
    Truck,
    Film,
    Building,
    Sparkles,
} from "lucide-react";
import { SectionHeading } from "../components/shared/SectionHeading";
import CTABanner from "../components/shared/CTABanner";
import { INDUSTRY_SOLUTIONS } from "../lib/industries";

const FloatingKnot = lazy(() => import("../components/three/FloatingKnot"));

const ICONS = {
    Landmark,
    HeartPulse,
    Factory,
    Zap,
    ShoppingBag,
    Plane,
    GraduationCap,
    Building2,
    Radio,
    Truck,
    Film,
    Building,
    Sparkles,
};

/**
 * Industries — editorial chapter layout
 * - Sticky chapter index (left) on desktop, scroll-spy on active section
 * - Alternating image side per chapter, with massive numbered identity
 * - 3D accent on hero, parallax image reveal
 */
export default function Industries() {
    const [activeId, setActiveId] = useState(INDUSTRY_SOLUTIONS[0]?.id);
    const containerRef = useRef(null);

    useEffect(() => {
        const sections = INDUSTRY_SOLUTIONS.map((ind) =>
            document.getElementById(`industry-${ind.id}`)
        ).filter(Boolean);
        if (!sections.length) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        const id = e.target.id.replace("industry-", "");
                        setActiveId(id);
                    }
                });
            },
            { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
        );
        sections.forEach((s) => obs.observe(s));
        return () => obs.disconnect();
    }, []);

    return (
        <main
            data-testid="page-industries"
            ref={containerRef}
            className="relative pt-32 md:pt-40"
        >
            {/* HERO */}
            <section className="relative pb-16 overflow-hidden">
                <div className="absolute inset-0 -z-10 grid-lines opacity-40" />
                <div className="absolute right-0 top 10 -z-10 h-[420px] w-[420px] hidden md:block opacity-70">
                    <Suspense fallback={null}>
                        <FloatingKnot color="#1E88E5" accent="#7FD03A" speed={0.22} />
                    </Suspense>
                </div>
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Industries we serve"
                        title={
                            <>
                                Thirteen sectors.{" "}
                                <span className="text-gradient-brand">
                                    One accountable partner.
                                </span>
                            </>
                        }
                        subtitle="From BFSI and Healthcare to Quantum-ready AI foundations we bring proven patterns, references and accelerators to every industry we serve."
                    />

                    {/* Industry ticker / quick jump rail */}
                    <div
                        data-testid="industry-rail"
                        className="mt-12 -mx-5 md:mx-0 overflow-x-auto scrollbar-hide"
                    >
                        <div className="flex items-stretch gap-2.5 px-5 md:px-0 pb-2 min-w-max">
                            {INDUSTRY_SOLUTIONS.map((ind, i) => {
                                const Icon = ICONS[ind.icon] || Sparkles;
                                const isActive = activeId === ind.id;
                                return (
                                    <a
                                        key={ind.id}
                                        href={`#industry-${ind.id}`}
                                        data-testid={`industry-rail-${ind.id}`}
                                        className={`group inline-flex items-center gap-2.5 rounded-full border px-4 py-2 text-[12.5px] transition-all whitespace-nowrap ${isActive
                                            ? "border-brand-green/60 bg-brand-green/10 text-white"
                                            : "border-white/10 glass text-zinc-400 hover:text-white hover:border-white/30"
                                            }`}
                                    >
                                        <span className="font-mono text-[10.5px] text-brand-green">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <Icon className="h-3.5 w-3.5" />
                                        {ind.shortName || ind.name}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* CHAPTERS */}
            <section className="relative py-12">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
                    {/* Sticky chapter index — desktop */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-32">
                            <div className="text-[11px] text-brand-green uppercase tracking-[0.22em] mb-5">
                                Chapter index
                            </div>
                            <ul
                                data-testid="industry-chapter-index"
                                className="space-y-1.5 border-l border-white/10"
                            >
                                {INDUSTRY_SOLUTIONS.map((ind, i) => {
                                    const isActive = activeId === ind.id;
                                    return (
                                        <li key={ind.id}>
                                            <a
                                                href={`#industry-${ind.id}`}
                                                data-testid={`industry-chapter-link-${ind.id}`}
                                                className={`group flex items-center gap-3 -ml-px pl-4 py-1.5 border-l-2 transition-all ${isActive
                                                    ? "border-brand-green text-white"
                                                    : "border-transparent text-zinc-500 hover:text-zinc-200"
                                                    }`}
                                            >
                                                <span className="font-mono text-[10.5px] text-brand-green w-6">
                                                    {String(i + 1).padStart(2, "0")}
                                                </span>
                                                <span className="text-[13px] leading-tight">
                                                    {ind.shortName || ind.name}
                                                </span>
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </aside>

                    {/* Chapters — alternating editorial blocks */}
                    <div className="lg:col-span-9 space-y-24 md:space-y-28">
                        {INDUSTRY_SOLUTIONS.map((ind, i) => (
                            <Chapter key={ind.id} ind={ind} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative py-16 md:py-20">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <CTABanner
                        eyebrow="Your industry next"
                        title="Get an industry-specific briefing."
                        subtitle="We'll share the pattern library, references and accelerators relevant to your sector."
                    />
                </div>
            </section>
        </main>
    );
}

function Chapter({ ind, index }) {
    const Icon = ICONS[ind.icon] || Sparkles;
    const flip = index % 2 === 1;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

    return (
        <article
            id={`industry-${ind.id}`}
            ref={ref}
            data-testid={`industry-chapter-${ind.id}`}
            className="relative scroll-mt-28"
        >
            {/* Massive chapter number watermark */}
            <div
                aria-hidden
                className={`pointer-events-none absolute -top-6 ${flip ? "right-0 md:right-2" : "left-0 md:-left-2"
                    } font-display font-bold leading-none select-none text-[120px] md:text-[180px] lg:text-[220px] tracking-tighter`}
                style={{
                    color: "transparent",
                    WebkitTextStroke: "1px rgba(127, 208, 58, 0.18)",
                }}
            >
                {String(index + 1).padStart(2, "0")}
            </div>

            <div
                className="relative grid grid-cols-1 md:grid-cols-12 gap-7 md:gap-9 items-center"
            >
                {/* Image / visual block */}
                <div className={`md:col-span-6 relative ${flip ? "md:order-2" : ""}`}>
                    <motion.div style={{ y }} className="relative">
                        <div className="relative overflow-hidden rounded-[28px] border border-white/10 aspect-[5/4]">
                            <img
                                src={ind.image}
                                alt={ind.name}
                                className="absolute inset-0 h-full w-full object-cover opacity-80 hover:opacity-100 hover:scale-105 transition-all duration-1000"
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-ink-950 via-ink-950/30 to-transparent" />
                            <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full glass-strong border border-white/15 px-3.5 py-1.5">
                                <Icon className="h-3.5 w-3.5 text-brand-green" />
                                <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-200">
                                    {ind.shortName || "Industry"}
                                </span>
                            </div>
                            <div className="absolute bottom-5 right-5 font-mono text-[11px] text-zinc-300/80">
                                Chapter {String(index + 1).padStart(2, "0")} / {INDUSTRY_SOLUTIONS.length}
                            </div>
                        </div>
                        {/* Decorative offset frame */}
                        <div
                            className={`absolute hidden md:block ${flip ? "-left-4" : "-right-4"
                                } -bottom-4 w-32 h-32 rounded-2xl border border-brand-green/30 bg-brand-green/[0.04]`}
                            aria-hidden
                        />
                    </motion.div>
                </div>

                {/* Copy block */}
                <div className={`md:col-span-6 relative ${flip ? "md:order-1" : ""}`}>
                    <div className="font-mono text-[11px] text-brand-green tracking-[0.22em] uppercase mb-3">
                        Chapter {String(index + 1).padStart(2, "0")}
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl lg:text-[44px] font-semibold tracking-tight leading-[1.05]">
                        {ind.name}
                    </h2>
                    <p className="mt-5 text-[15px] text-zinc-400 leading-relaxed max-w-xl">
                        {ind.overview}
                    </p>

                    {/* Solutions chip rail */}
                    <div className="mt-7">
                        <div className="text-[10.5px] text-zinc-500 uppercase tracking-[0.22em] mb-3">
                            Key solutions ({ind.solutions.length})
                        </div>
                        <div className="flex flex-wrap gap-2 max-w-xl">
                            {ind.solutions.slice(0, 6).map((s) => (
                                <span
                                    key={s.title}
                                    className="text-[12px] glass border border-white/10 rounded-full px-3 py-1.5 text-zinc-300"
                                >
                                    {s.title}
                                </span>
                            ))}
                            {ind.solutions.length > 6 && (
                                <span className="text-[12px] rounded-full px-3 py-1.5 text-zinc-500">
                                    +{ind.solutions.length - 6} more
                                </span>
                            )}
                        </div>
                    </div>

                    <Link
                        to={`/products?industry=${ind.id}`}
                        data-testid={`industry-cta-${ind.id}`}
                        className="mt-9 inline-flex items-center gap-2 rounded-full bg-brand-linear px-5 py-3 text-[13.5px] font-medium text-white hover:shadow-glow-brand transition-all"
                    >
                        Explore {ind.shortName || ind.name} solutions
                        <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <Link
                        to={`/case-studies`}
                        className="mt-9 ml-2 inline-flex items-center gap-1.5 text-[13px] text-zinc-400 hover:text-white transition-colors px-3 py-3"
                    >
                        See proof
                        <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
            </div>
        </article>
    );
}
